import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Spinner,
  TableCellLayout,
  TableColumnDefinition,
  Text,
} from '@fluentui/react-components';
import { Add24Regular, Delete24Regular, Edit24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { ContractNegotiationConcept } from '../../../models/Entities/Contracts/ContractNegotiationConcept';
import { NewContractNegotiationConcept } from '../../../models/Entities/Contracts/NewContractNegotiationConcept';
import * as ComponentFunctions from './ContractNegotiationConceptList.Functions';
import { useContractNegotiationConceptListStyles } from './ContractNegotiationConceptList.Styles';
import ContractNegotiationConceptFormComponent from '../ContractNegotiationConceptForm/ContractNegotiationConceptForm.Component';
import { mapStateToProps, mapDispatchToProps } from './ContractNegotiationConceptList.Functions';

const formatCurrency = (value: number | undefined): string => {
  if (typeof value !== 'number') return '-';
  return value.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
};

const formatVariance = (value: number | undefined): string => {
  if (typeof value !== 'number') return '-';
  return `${value.toFixed(2)}%`;
};

const ContractNegotiationConceptListComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const styles = useContractNegotiationConceptListStyles();

  const [isConceptFormOpen, setIsConceptFormOpen] = useState<boolean>(false);
  const [selectedConcept, setSelectedConcept] = useState<ContractNegotiationConcept | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<ContractNegotiationConcept | undefined>(undefined);

  useEffect(() => {
    props.GetContractNegotiationConcepts(props.contractNegotiationId, props.contractId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.contractNegotiationId, props.contractId]);

  const handleAddNewConcept = () => {
    setSelectedConcept(undefined);
    setIsConceptFormOpen(true);
  };

  const handleEditConcept = (concept: ContractNegotiationConcept) => {
    setSelectedConcept(concept);
    setIsConceptFormOpen(true);
  };

  const handleConceptFormDismiss = () => {
    setIsConceptFormOpen(false);
    setSelectedConcept(undefined);
  };

  const handleConceptSave = (entity: NewContractNegotiationConcept | ContractNegotiationConcept) => {
    const isExisting = 'id' in entity && !!(entity as ContractNegotiationConcept).id;
    if (isExisting) {
      props.UpdateContractNegotiationConcept(entity as ContractNegotiationConcept, props.contractId);
    } else {
      props.CreateContractNegotiationConcept(entity as NewContractNegotiationConcept, props.contractId);
    }
    setIsConceptFormOpen(false);
    setSelectedConcept(undefined);
  };

  const handleDeleteRequest = (concept: ContractNegotiationConcept) => {
    setDeleteTarget(concept);
  };

  const handleDeleteConfirm = () => {
    if (deleteTarget) {
      props.DeleteContractNegotiationConcept(
        deleteTarget.id,
        props.contractId,
        deleteTarget.contractNegotiationId
      );
    }
    setDeleteTarget(undefined);
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(undefined);
  };

  const columns: TableColumnDefinition<ContractNegotiationConcept>[] = useMemo(
    () => [
      createTableColumn<ContractNegotiationConcept>({
        columnId: 'conceptId',
        renderHeaderCell: () => t('contractNegotiationConceptList.columns.conceptId', 'ID Concepto'),
        renderCell: (item) => <TableCellLayout>{item.conceptId}</TableCellLayout>,
      }),
      createTableColumn<ContractNegotiationConcept>({
        columnId: 'currencyCode',
        renderHeaderCell: () => t('contractNegotiationConceptList.columns.currency', 'Moneda'),
        renderCell: (item) => <TableCellLayout>{item.currencyCode || '-'}</TableCellLayout>,
      }),
      createTableColumn<ContractNegotiationConcept>({
        columnId: 'negotiationReferenceCost',
        renderHeaderCell: () => t('contractNegotiationConceptList.columns.referenceCost', 'Costo referencia'),
        renderCell: (item) => <TableCellLayout>{formatCurrency(item.negotiationReferenceCost)}</TableCellLayout>,
      }),
      createTableColumn<ContractNegotiationConcept>({
        columnId: 'baselineVariance',
        renderHeaderCell: () => t('contractNegotiationConceptList.columns.variance', 'Variacion base'),
        renderCell: (item) => <TableCellLayout>{formatVariance(item.baselineVariance)}</TableCellLayout>,
      }),
      createTableColumn<ContractNegotiationConcept>({
        columnId: 'actions',
        renderHeaderCell: () => t('contractNegotiationConceptList.columns.actions', 'Acciones'),
        renderCell: (item) => (
          <TableCellLayout>
            <div className={styles.actionCell}>
              <Button
                appearance="subtle"
                size="small"
                icon={<Edit24Regular />}
                onClick={() => handleEditConcept(item)}
                title={t('contractNegotiationConceptList.actions.edit', 'Editar')}
              />
              <Button
                appearance="subtle"
                size="small"
                icon={<Delete24Regular />}
                onClick={() => handleDeleteRequest(item)}
                title={t('contractNegotiationConceptList.actions.delete', 'Eliminar')}
              />
            </div>
          </TableCellLayout>
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, styles]
  );

  if (props.isLoading) {
    return (
      <div className={styles.root}>
        <Spinner size="small" label={t('contractNegotiationConceptList.loading', 'Cargando conceptos...')} />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <Text weight="semibold" size={300}>
          {t('contractNegotiationConceptList.title', 'Conceptos de negociacion')}
        </Text>
        <Button appearance="primary" size="small" icon={<Add24Regular />} onClick={handleAddNewConcept}>
          {t('contractNegotiationConceptList.addButton', 'Agregar concepto')}
        </Button>
      </div>

      {props.errorMessage && (
        <MessageBar intent="error">
          <MessageBarBody>{props.errorMessage}</MessageBarBody>
        </MessageBar>
      )}

      {!props.isLoading && !props.errorMessage && props.concepts.length === 0 && (
        <div className={styles.emptyState}>
          <Text>{t('contractNegotiationConceptList.empty', 'No hay conceptos registrados.')}</Text>
        </div>
      )}

      {props.concepts.length > 0 && (
        <div className={styles.gridWrapper}>
          <DataGrid
            items={props.concepts}
            columns={columns}
            sortable
            resizableColumns
            focusMode="composite"
            getRowId={(item) => String(item.id)}
          >
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell }) => (
                  <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody<ContractNegotiationConcept>>
              {({ item, rowId }) => (
                <DataGridRow<ContractNegotiationConcept> key={rowId}>
                  {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
        </div>
      )}

      <ContractNegotiationConceptFormComponent
        contractNegotiationId={props.contractNegotiationId}
        contractId={props.contractId}
        concept={selectedConcept}
        isOpen={isConceptFormOpen}
        onDismiss={handleConceptFormDismiss}
        onSave={handleConceptSave}
      />

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(_, data) => { if (!data.open) handleDeleteCancel(); }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('contractNegotiationConceptList.deleteDialog.title', 'Eliminar concepto')}</DialogTitle>
            <DialogContent>
              {t(
                'contractNegotiationConceptList.deleteDialog.message',
                'Esta seguro que desea eliminar este concepto de negociacion?'
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={handleDeleteCancel}>
                {t('contractNegotiationConceptList.deleteDialog.cancel', 'Cancelar')}
              </Button>
              <Button appearance="primary" onClick={handleDeleteConfirm}>
                {t('contractNegotiationConceptList.deleteDialog.confirm', 'Eliminar')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export { ContractNegotiationConceptListComponent };
export default connect(mapStateToProps, mapDispatchToProps)(ContractNegotiationConceptListComponent as React.ComponentType<ComponentFunctions.OwnProps>);